import { cn } from '@/shared/lib';
import type { StatCardProps, StatCardTone } from './types';
import {
  statCardCaptionStyles,
  statCardLabelStyles,
  statCardStyles,
  statCardToneStyles,
  statCardValueStyles,
} from './styles';

/**
 * A single number with its label.
 *
 * WHY A PRIMITIVE, AND WHY IN `shared/`. The dashboard is the first surface that needs a KPI
 * tile, and there was no card in the primitive set — so the first implementation would have been
 * a div with padding, written inline, and copied. This is that primitive, so the second surface
 * gets the same card rather than a near-copy.
 *
 * THE CARD IS NOT A CONTROL UNLESS IT HAS A DESTINATION. Without `href` the card renders a
 * `<div>`: a card that looks clickable but does nothing is worse than a card that does not look
 * clickable. With `href` it renders a plain `<a>`, the same choice `shared/app-detail-layout`
 * makes — a real anchor that middle-click, "open in new tab" and "copy link" all behave the way a
 * user expects, and a shared primitive that does not need a router to render.
 *
 * THE TONE IS A REINFORCEMENT. The label and the value carry the meaning; the tone only
 * confirms it. Nothing here is conveyed by colour alone.
 */
export function StatCard({
  label,
  value,
  caption,
  tone = 'neutral',
  href,
  testId,
  className,
  ...rest
}: StatCardProps): React.JSX.Element {
  const surfaceClass = cn(
    statCardStyles({ interactive: href !== undefined }),
    className,
  );

  const body = (
    <>
      <span className={statCardLabelStyles}>{label}</span>
      <span className={cn(statCardValueStyles, statCardToneStyles[tone])}>{value}</span>
      {caption === undefined ? null : (
        <span className={statCardCaptionStyles}>{caption}</span>
      )}
    </>
  );

  if (href !== undefined) {
    return (
      <a href={href} className={surfaceClass} data-testid={testId} {...rest}>
        {body}
      </a>
    );
  }

  return (
    <div className={surfaceClass} data-testid={testId} {...rest}>
      {body}
    </div>
  );
}

export type { StatCardProps, StatCardTone };
